import { createPortal } from 'react-dom';
import { CloseButton } from '@/common/components/CloseButton';

interface WritableCalendar {
  id: string;
  name: string;
  primary: boolean;
  accessRole: string;
}

/**
 * Quick-add ("New Event") form modal — natural-language event creation plus a
 * writable-calendar picker and an "Add manually" escape hatch. Holds no
 * state/hooks of its own; every value is threaded in as a typed prop.
 */
export function EventQuickAddModal({
  moduleId,
  quickAddText,
  setQuickAddText,
  quickAddCalendarId,
  setQuickAddCalendarId,
  quickAddRef,
  allWritableCalendars,
  newEventInitialDate,
  getPlaceholder,
  handleQuickAdd,
  handleBack,
  setShowManualEntry,
}: {
  moduleId: string;
  quickAddText: string;
  setQuickAddText: (value: string) => void;
  quickAddCalendarId: string;
  setQuickAddCalendarId: (value: string) => void;
  quickAddRef: React.RefObject<HTMLInputElement | null>;
  allWritableCalendars: WritableCalendar[];
  newEventInitialDate: Date | null;
  getPlaceholder: (hasDate: boolean) => string;
  handleQuickAdd: () => void;
  handleBack: () => void;
  setShowManualEntry: (value: boolean) => void;
}) {
  const portalRoot = document.getElementById('modal-portal-root');
  if (!portalRoot) return null;

  const modalTitle = (() => {
    if (newEventInitialDate) {
      const d = newEventInitialDate;
      const dayStr = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
      const h = d.getHours();
      if (h !== 9 || d.getMinutes() !== 0) {
        // Specific time was clicked (day/week view)
        const timeStr = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }).toLowerCase();
        return `New Event — ${dayStr} ${timeStr}`;
      }
      return `New Event — ${dayStr}`;
    }
    return 'New Event';
  })();

  return createPortal(
    <div className="event-modal-overlay" onClick={handleBack}>
      <div className="event-modal-container" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
        <div style={{ padding: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <h2 style={{ fontSize: 16, fontWeight: 600, color: 'var(--color-text-heading)', margin: 0 }}>{modalTitle}</h2>
            <CloseButton onClick={handleBack} label="Close event editor" />
          </div>
          {allWritableCalendars.length > 0 && (
            <select
              value={quickAddCalendarId || allWritableCalendars.find(c => c.primary)?.id || allWritableCalendars[0]?.id || ''}
              onChange={(e) => {
                setQuickAddCalendarId(e.target.value);
                localStorage.setItem(`xavior:last-calendar:${moduleId}`, e.target.value);
              }}
              style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--color-border, #e0e0e0)', borderRadius: 8, fontSize: 14, fontFamily: "'DM Sans', sans-serif", color: 'var(--color-text)', background: 'var(--color-surface, white)', marginBottom: 10, boxSizing: 'border-box' }}
            >
              {allWritableCalendars.map((cal) => (
                <option key={cal.id} value={cal.id}>
                  {cal.name}{cal.primary ? ' (Primary)' : ''}
                </option>
              ))}
            </select>
          )}
          <textarea
            ref={quickAddRef as any}
            autoFocus
            value={quickAddText}
            onChange={(e) => setQuickAddText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleQuickAdd(); } }}
            placeholder={getPlaceholder(!!newEventInitialDate)}
            rows={3}
            style={{ width: '100%', padding: '12px', border: '1px solid var(--color-border, #e0e0e0)', borderRadius: 10, fontSize: 15, fontFamily: "'DM Sans', sans-serif", outline: 'none', color: 'var(--color-text)', background: 'var(--color-surface, white)', resize: 'none', lineHeight: 1.5, boxSizing: 'border-box' }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
            <div>
              <button
                  onClick={() => { handleBack(); setShowManualEntry(true); }}
                  style={{ padding: '8px 0', background: 'none', border: 'none', fontSize: 13, fontWeight: 500, cursor: 'pointer', color: 'var(--color-primary, #1a9ba5)', fontFamily: "'DM Sans', sans-serif" }}
                >Add manually</button>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleBack} style={{ padding: '8px 20px', borderRadius: 8, border: '1px solid var(--color-border)', background: 'none', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", color: 'var(--color-text)' }}>Cancel</button>
              <button onClick={handleQuickAdd} disabled={!quickAddText.trim()} style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: 'var(--color-primary, #1a9ba5)', color: 'white', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}>Save</button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    portalRoot,
  );
}
